export type PaletteStep =
  | 25
  | 50
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 950;
export type PaletteFamilyName = "gray" | "red" | "orange" | "green" | "indigo";

export type PaletteFamily = Record<PaletteStep, string>;

export type ColorPalette = Record<PaletteFamilyName, PaletteFamily>;

export const palette: ColorPalette = {
  gray: {
    25: "#fcfcfd",
    50: "#f8fafc",
    100: "#eef2f6",
    200: "#e3e8ef",
    300: "#cdd5df",
    400: "#9aa4b2",
    500: "#697586",
    600: "#4b5565",
    700: "#364152",
    800: "#202939",
    900: "#121926",
    950: "#0d121c",
  },
  red: {
    25: "#fffbfa",
    50: "#fef3f2",
    100: "#fee4e2",
    200: "#fecdca",
    300: "#fda29b",
    400: "#f97066",
    500: "#f04438",
    600: "#d92d20",
    700: "#b42318",
    800: "#912018",
    900: "#7a271a",
    950: "#55160c",
  },
  orange: {
    25: "#fffcf5",
    50: "#fffaeb",
    100: "#fef0c7",
    200: "#fedf89",
    300: "#fec84b",
    400: "#fdb022",
    500: "#f79009",
    600: "#dc6803",
    700: "#b54708",
    800: "#93370d",
    900: "#7a2e0e",
    950: "#4e1d09",
  },
  green: {
    25: "#f6fef9",
    50: "#ecfdf3",
    100: "#dcfae6",
    200: "#abefc6",
    300: "#75e0a7",
    400: "#47cd89",
    500: "#17b26a",
    600: "#079455",
    700: "#067647",
    800: "#085d3a",
    900: "#074d31",
    950: "#053321",
  },
  indigo: {
    25: "#f5f8ff",
    50: "#eef4ff",
    100: "#e0eaff",
    200: "#c7d7fe",
    300: "#a4bcfd",
    400: "#8098f9",
    500: "#6172f3",
    600: "#444ce7",
    700: "#3538cd",
    800: "#2d31a6",
    900: "#2d3282",
    950: "#1f235b",
  },
};

// Figma Semantic Color Variables (exact naming from Figma)
export type FigmaTextColors = {
  "text-primary-900": string;
  "text-secondary-700": string;
  "text-tertiary-600": string;
  "text-quaternary-500": string;
  "text-white": string;
  "text-disabled-400": string;
  "text-placeholder-400": string;
  "text-brand-600": string;
  "text-error-600": string;
  "text-warning-600": string;
  "text-success-600": string;
};

export type FigmaBackgroundColors = {
  "bg-primary": string;
  "bg-secondary": string;
  "bg-tertiary": string;
  "bg-quaternary": string;
  "bg-active": string;
  "bg-disabled": string;
  "bg-overlay": string;
  "bg-brand-50": string;
  "bg-brand-600": string;
  "bg-error-50": string;
  "bg-error-600": string;
  "bg-warning-50": string;
  "bg-warning-600": string;
  "bg-success-50": string;
  "bg-success-600": string;
};

export type FigmaBorderColors = {
  "border-primary": string;
  "border-secondary": string;
  "border-tertiary": string;
  "border-disabled": string;
  "border-brand": string;
  "border-error": string;
  "border-warning": string;
  "border-success": string;
};

export type FigmaColors = FigmaTextColors &
  FigmaBackgroundColors &
  FigmaBorderColors;

export type ThemeColors = {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  success: string;
  successForeground: string;
  warning: string;
  warningForeground: string;
  info: string;
  infoForeground: string;
  border: string;
  input: string;
  ring: string;
};

// Figma Color System - Light & Dark themes (exact values from Figma)
export const figmaColors: { light: FigmaColors; dark: FigmaColors } = {
  light: {
    // Text Colors (Figma exact values)
    "text-primary-900": "#424650", // Ana metin rengi
    "text-secondary-700": "#414651", // İkincil metin rengi (sidebar'da kullanılan)
    "text-tertiary-600": "#535862", // Üçüncül metin rengi
    "text-quaternary-500": "#9ca4b1", // Dördüncül metin rengi
    "text-white": "#ffffff", // Beyaz metin
    "text-disabled-400": "#9aa4b2", // Devre dışı metin
    "text-placeholder-400": "#9aa4b2", // Placeholder metin
    "text-brand-600": "#6172f3", // Marka rengi metin
    "text-error-600": "#d92d20", // Hata metin rengi
    "text-warning-600": "#dc6803", // Uyarı metin rengi
    "text-success-600": "#079455", // Başarı metin rengi

    // Background Colors
    "bg-primary": "#ffffff", // Ana arka plan
    "bg-secondary": "#fafafa", // İkincil arka plan
    "bg-tertiary": "#f8fafc", // Üçüncül arka plan
    "bg-quaternary": "#eef2f6", // Dördüncül arka plan
    "bg-active": "#e0eaff", // Aktif durum arka plan
    "bg-disabled": "#f8fafc", // Devre dışı arka plan
    "bg-overlay": "rgba(18, 25, 38, 0.6)", // Overlay arka plan
    "bg-brand-50": "#eef4ff", // Marka açık arka plan
    "bg-brand-600": "#6172f3", // Marka koyu arka plan
    "bg-error-50": "#fef3f2", // Hata açık arka plan
    "bg-error-600": "#d92d20", // Hata koyu arka plan
    "bg-warning-50": "#fffaeb", // Uyarı açık arka plan
    "bg-warning-600": "#dc6803", // Uyarı koyu arka plan
    "bg-success-50": "#ecfdf3", // Başarı açık arka plan
    "bg-success-600": "#079455", // Başarı koyu arka plan

    // Border Colors
    "border-primary": "#e3e8ef", // Ana border
    "border-secondary": "#e9eaeb", // İkincil border (sidebar'da kullanılan)
    "border-tertiary": "#cdd5df", // Üçüncül border
    "border-disabled": "#e3e8ef", // Devre dışı border
    "border-brand": "#6172f3", // Marka border
    "border-error": "#d92d20", // Hata border
    "border-warning": "#dc6803", // Uyarı border
    "border-success": "#079455", // Başarı border
  },
  dark: {
    // Text Colors (Dark theme) - Monochromatic Minimalism
    "text-primary-900": "#E0E0E0", // Ana metin rengi - Light gray
    "text-secondary-700": "#B0B0B0", // İkincil metin rengi - Medium gray
    "text-tertiary-600": "#888888", // Üçüncül metin rengi - Soft gray
    "text-quaternary-500": "#666666", // Dördüncül metin rengi - Muted gray
    "text-white": "#ffffff", // Beyaz metin
    "text-disabled-400": "#444444", // Devre dışı metin - Dark gray
    "text-placeholder-400": "#666666", // Placeholder metin
    "text-brand-600": "#888888", // Marka rengi metin - Soft gray accent
    "text-error-600": "#f97066", // Hata metin rengi - koyu zeminde 400 tonu
    "text-warning-600": "#fdb022", // Uyarı metin rengi - koyu zeminde 400 tonu
    "text-success-600": "#47cd89", // Başarı metin rengi - koyu zeminde 400 tonu

    // Background Colors - Monochromatic Minimalism (Enhanced)
    "bg-primary": "#121212", // Ana arka plan - Charcoal black
    "bg-secondary": "#1a1a1a", // İkincil arka plan - Dark gray (sidebar)
    "bg-tertiary": "#2a2a2a", // Üçüncül arka plan - Medium dark gray (cards)
    "bg-quaternary": "#333333", // Dördüncül arka plan - Lighter gray (input areas)
    "bg-active": "#333333", // Aktif durum arka plan - Lighter gray
    "bg-disabled": "#1a1a1a", // Devre dışı arka plan
    "bg-overlay": "rgba(18, 18, 18, 0.9)", // Overlay arka plan
    "bg-brand-50": "#2a2a2a", // Marka açık arka plan - Medium dark gray
    "bg-brand-600": "#666666", // Marka koyu arka plan - Medium gray
    "bg-error-50": "#55160c", // Hata tint yüzey
    "bg-error-600": "#f04438", // Hata dolgu
    "bg-warning-50": "#4e1d09", // Uyarı tint yüzey
    "bg-warning-600": "#f79009", // Uyarı dolgu
    "bg-success-50": "#053321", // Başarı tint yüzey
    "bg-success-600": "#17b26a", // Başarı dolgu

    // Border Colors - Monochromatic Minimalism (Enhanced)
    "border-primary": "#333333", // Ana border - Lighter gray for better visibility
    "border-secondary": "#444444", // İkincil border - Dark gray
    "border-tertiary": "#555555", // Üçüncül border - Medium gray
    "border-disabled": "#2a2a2a", // Devre dışı border
    "border-brand": "#666666", // Marka border - Medium gray
    "border-error": "#f04438", // Hata border
    "border-warning": "#f79009", // Uyarı border
    "border-success": "#17b26a", // Başarı border
  },
};

export const theme: { light: ThemeColors; dark: ThemeColors } = {
  light: {
    background: palette.gray[25],
    foreground: palette.gray[900],
    card: palette.gray[25],
    cardForeground: palette.gray[900],
    popover: palette.gray[25],
    popoverForeground: palette.gray[900],
    primary: palette.indigo[600],
    primaryForeground: "#ffffff",
    secondary: palette.gray[100],
    secondaryForeground: palette.gray[900],
    muted: palette.gray[100],
    mutedForeground: palette.gray[500],
    accent: palette.indigo[500],
    accentForeground: "#ffffff",
    destructive: palette.red[600],
    destructiveForeground: "#ffffff",
    success: palette.green[600],
    successForeground: "#ffffff",
    warning: palette.orange[600],
    warningForeground: "#ffffff",
    info: palette.indigo[500],
    infoForeground: "#ffffff",
    border: palette.gray[200],
    input: palette.gray[200],
    ring: palette.indigo[500],
  },
  dark: {
    background: "#121212", // Charcoal black - Main background
    foreground: "#e0e0e0", // Light gray - High contrast text
    card: "#1a1a1a", // Dark gray - Sidebar background
    cardForeground: "#e0e0e0",
    popover: "#2a2a2a", // Medium dark gray - Cards and popups
    popoverForeground: "#e0e0e0",
    primary: "#666666", // Medium gray - Primary buttons
    primaryForeground: "#121212",
    secondary: "#2a2a2a", // Medium dark gray - Secondary elements
    secondaryForeground: "#e0e0e0",
    muted: "#1a1a1a", // Dark gray - Muted backgrounds
    mutedForeground: "#888888", // Soft gray - Muted text
    accent: "#666666", // Medium gray - Accent elements
    accentForeground: "#121212",
    // Durum renkleri koyu temada da kromatik kalmalı: griye düşerlerse
    // hata metni yardımcı metinden ayırt edilemiyor. Koyu zeminde okunurluk
    // için 600 yerine 500 tonları kullanılır.
    destructive: palette.red[500],
    destructiveForeground: "#ffffff",
    success: palette.green[500],
    successForeground: "#ffffff",
    warning: palette.orange[500],
    warningForeground: "#ffffff",
    info: "#666666", // Medium gray - Info
    infoForeground: "#121212",
    border: "#333333", // Lighter gray - Better visible borders
    input: "#2a2a2a", // Medium dark gray - Input background
    ring: "#666666", // Medium gray - Focus ring
  },
};

// CSS Variables writer for Figma colors
export const writeFigmaColorVars = (
  root: HTMLElement,
  scheme: "light" | "dark"
) => {
  const colors = figmaColors[scheme];

  Object.entries(colors).forEach(([key, value]) => {
    // Convert key names to Figma format:
    // text-secondary-700 → --colors-text-text-secondary-700
    // bg-primary → --colors-background-bg-primary
    // border-primary → --colors-border-border-primary
    let cssVarName: string;

    if (key.startsWith("text-")) {
      cssVarName = `--colors-text-${key}`;
    } else if (key.startsWith("bg-")) {
      cssVarName = `--colors-background-${key}`;
    } else if (key.startsWith("border-")) {
      cssVarName = `--colors-border-${key}`;
    } else {
      cssVarName = `--colors-${key}`;
    }

    root.style.setProperty(cssVarName, value);
  });
};

// Backward compatible export used by UI store
export const colors = {
  light: theme.light,
  dark: theme.dark,
} as const;

export type ColorScheme = keyof typeof colors;
export type ColorToken = keyof (typeof colors)[ColorScheme];
